import React, { useEffect, useRef, useState } from "react";
import "./StockScanner.css";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";

const toNumber = (value) => {
    const parsedValue = Number.parseFloat(
        String(value ?? "")
            .replace(/,/g, "")
            .replace("%", "")
    );

    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatNumber = (value) => {
    const numericValue = toNumber(value);

    if (numericValue == null) {
        return value ?? "--";
    }

    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const SYMBOL_SERIES_SUFFIX_SET = new Set(["EQ", "BE", "BZ", "BL", "SM", "ST"]);

const normalizeSymbol = (value) => {
    const rawValue = String(value ?? "").trim().toUpperCase();

    if (!rawValue) {
        return "";
    }

    const exchangeTrimmedValue = rawValue.includes(":")
        ? rawValue.split(":").pop()?.trim() ?? ""
        : rawValue;
    const symbolParts = exchangeTrimmedValue.split("-");

    if (symbolParts.length > 1) {
        const lastPart = symbolParts[symbolParts.length - 1];

        if (SYMBOL_SERIES_SUFFIX_SET.has(lastPart)) {
            return symbolParts.slice(0, -1).join("-");
        }
    }

    return exchangeTrimmedValue;
};

const normalizeScannerKey = (value) => String(value ?? "").trim().toUpperCase();

const MID_CROSS_BUY = "MID_CROSS_BUY";
const MID_CROSS_SELL = "MID_CROSS_SELL";

const MID_CROSS_BUY_TAB = "MID_CROSS_BUY_TAB";
const MID_CROSS_SELL_TAB = "MID_CROSS_SELL_TAB";
const MC_FILTER_TAB = "MC_FILTER_TAB";
const MC_BUY_FILTER_TAB = "MC_BUY_FILTER_TAB";
const MC_SELL_FILTER_TAB = "MC_SELL_FILTER_TAB";

const SCANNER_SECTION_TITLE_MAP = {
    [MID_CROSS_BUY]: "MID CROSS BUY",
    [MID_CROSS_SELL]: "MID CROSS SELL",
    [MC_FILTER_TAB]: "MC FILTER",
    [MC_BUY_FILTER_TAB]: "MC BUY FILTER",
    [MC_SELL_FILTER_TAB]: "MC SELL FILTER",
};

const createSectorDetailsMap = (
    sectorData = [],
    isSectorMatch = () => false,
    restrictToSelectedStocks = true
) =>
    sectorData.reduce((accumulator, sector) => {
        const sectorPercent = toNumber(sector?.pChange);

        if (sectorPercent == null || !isSectorMatch(sectorPercent)) {
            return accumulator;
        }

        (sector?.sectorialIndicesStockModelList ?? []).forEach((stock) => {
            const symbol = normalizeSymbol(stock?.symbol);

            if (
                !symbol ||
                (restrictToSelectedStocks && !SECTOR_SELECTED_STOCK_SET.has(symbol)) ||
                accumulator.has(symbol)
            ) {
                return;
            }

            accumulator.set(symbol, {
                sectorName: sector?.index ?? "--",
                sectorPercent,
            });
        });

        return accumulator;
    }, new Map());

const createLotSizeBySymbolMap = (stockDayRangeData = []) =>
    stockDayRangeData.reduce((accumulator, stock) => {
        const symbol = normalizeSymbol(
            stock?.symbol ??
            stock?.stockSymbol ??
            stock?.tradingSymbol ??
            stock?.ticker
        );
        const lotSize =
            stock?.fno_lot_size ??
            stock?.fnoLotSize ??
            stock?.lot_size ??
            stock?.lotSize ??
            stock?.marketLot ??
            stock?.market_lot;

        if (!symbol || lotSize == null || accumulator.has(symbol)) {
            return accumulator;
        }

        accumulator.set(symbol, lotSize);
        return accumulator;
    }, new Map());

const attachSectorDetailsToStocks = (
    stocks = [],
    sectorDetailsBySymbol = new Map(),
    requireSectorDetails = true
) =>
    stocks
        .map((stock) => {
            const sectorDetails = sectorDetailsBySymbol.get(normalizeSymbol(stock.symbol));

            if (!sectorDetails && requireSectorDetails) {
                return null;
            }

            return {
                ...stock,
                sectorName: sectorDetails?.sectorName ?? stock?.sectorName ?? null,
                sectorPercent: sectorDetails?.sectorPercent ?? stock?.sectorPercent ?? null,
            };
        })
        .filter(Boolean);

const getScannerRowsByKey = (scannerDataSource, key) => {
    const normalizedKey = normalizeScannerKey(key);

    if (scannerDataSource instanceof Map) {
        if (Array.isArray(scannerDataSource.get(key))) {
            return scannerDataSource.get(key);
        }

        for (const [entryKey, entryValue] of scannerDataSource.entries()) {
            if (normalizeScannerKey(entryKey) === normalizedKey) {
                return Array.isArray(entryValue) ? entryValue : [];
            }
        }

        return [];
    }

    if (scannerDataSource && typeof scannerDataSource === "object" && !Array.isArray(scannerDataSource)) {
        if (Array.isArray(scannerDataSource[key])) {
            return scannerDataSource[key];
        }

        const matchedObjectKey = Object.keys(scannerDataSource).find(
            (entryKey) => normalizeScannerKey(entryKey) === normalizedKey
        );

        return matchedObjectKey && Array.isArray(scannerDataSource[matchedObjectKey])
            ? scannerDataSource[matchedObjectKey]
            : [];
    }

    return [];
};

const hasScannerEntries = (scannerDataSource) => {
    if (scannerDataSource instanceof Map) {
        return scannerDataSource.size > 0;
    }

    return Boolean(
        scannerDataSource &&
        typeof scannerDataSource === "object" &&
        !Array.isArray(scannerDataSource) &&
        Object.keys(scannerDataSource).length > 0
    );
};

const getFoTopRowsByKey = (foTopGainerLooserData, key) =>
    Array.isArray(foTopGainerLooserData?.[key]?.data)
        ? foTopGainerLooserData[key].data
        : [];

const createSymbolSet = (rows = []) =>
    new Set(
        rows
            .map((row) =>
                normalizeSymbol(
                    row?.symbol ??
                    row?.stockSymbol ??
                    row?.tradingSymbol ??
                    row?.ticker
                )
            )
            .filter(Boolean)
    );

const getRankedSectors = (sectorData = []) =>
    sectorData
        .map((sector) => ({
            sector,
            sectorPercent: toNumber(sector?.pChange),
        }))
        .filter((entry) => entry.sectorPercent != null)
        .sort((firstEntry, secondEntry) =>
            firstEntry.sectorPercent - secondEntry.sectorPercent
        );

const createSectorSymbolSet = (sectors = []) =>
    new Set(
        sectors
            .flatMap((sector) => sector?.sectorialIndicesStockModelList ?? [])
            .map((stock) => normalizeSymbol(stock?.symbol))
            .filter(Boolean)
    );

const createSectorNameSet = (sectors = []) =>
    new Set(
        sectors
            .map((sector) => String(sector?.index ?? "").trim().toUpperCase())
            .filter(Boolean)
    );

const filterStocksByFoTopAndSectors = (
    stocks = [],
    foTopSymbolSet = new Set(),
    sectorSymbolSet = new Set(),
    sectorNameSet = new Set()
) =>
    stocks.filter((stock) => {
        const symbol = normalizeSymbol(stock?.symbol);
        const sectorName = String(stock?.sectorName ?? "").trim().toUpperCase();

        return (
            symbol &&
            foTopSymbolSet.has(symbol) &&
            (sectorSymbolSet.has(symbol) || sectorNameSet.has(sectorName))
        );
    });

const normalizeScannerStocks = (stockScannerDataList = [], lotSizeBySymbol = new Map()) =>
    stockScannerDataList.map((stock, index) => {
        const symbol = normalizeSymbol(
            stock?.symbol ??
            stock?.stockSymbol ??
            stock?.tradingSymbol ??
            stock?.ticker ??
            "--"
        ) || "--";
        const fnoLotSize =
            stock?.fno_lot_size ??
            stock?.fnoLotSize ??
            stock?.lot_size ??
            stock?.lotSize ??
            stock?.marketLot ??
            stock?.market_lot ??
            lotSizeBySymbol.get(symbol) ??
            "--";

        return {
            symbolCount: stock?.symbolCount ?? stock?.symbol_count ?? stock?.symbolCnt ?? stock?.count ?? "--",
            symbol,
            status: String(stock?.status ?? "").trim().toUpperCase(),
            changePercent: toNumber(stock?.["%_change"] ?? stock?.changePercent),
            createdDate: stock?.createdDate ?? "--",
            createdTime: stock?.createdTime ?? "--",
            volume: stock?.volume,
            triggerDate: stock?.triggerDate ?? "--",
            triggerTime: stock?.triggerTime ?? "--",
            ltp: stock?.ltp ?? stock?.close,
            fnoLotSize,
            __scannerKey: `${stock?.symbol ?? stock?.stockSymbol ?? "stock"}-${index}`,
        };
    });

    const SECTOR_SELECTED_STOCK_SET = new Set([
        "UNOMINDA", "BAJAJ-AUTO", "TIINDIA", "BOSCHLTD", "SONACOMS", "TVSMOTOR", "EICHERMOT", "HEROMOTOCO", "M&M", "BHARATFORG",
        "FORCEMOTOR", "PFC", "RECLTD", "BAJAJFINSV", "MFSL", "LICHSGFIN", "JIOFIN", "CHOLAIFIN", "SHRIRAMFIN", "MUTHOOTFIN",
        "BSE", "SBIN", "BAJAJFINANCE", "DABUR", "TATACONSUM", "HINDUNILVR", "GODREJCP", "UNITEDSPR", "NESTLEIND", "ITC",
        "VBL", "UBL", "BRITANNIA", "COFORGE", "PERSISTENT", "LTM", "INFY", "HCLTECH", "TECHMM", "MPHASIS", "TCS", "OFSS",
        "NATIONALUM", "HINDCOPPER", "HINDZINC", "ADANIENT", "JSWSTEEL", "JINDALSTEL", "HINDALCO", "APLAPOLLO", "TATASTEEL",
        "VEDL", "MANKIND", "LAURUSLABS", "SUNPHARMA", "TORNTPHARM", "AUROPHARMA", "DIVISLAB", "GLENMARK", "ALKEM", "BIOCON",
        "DRREDDY", "CIPLA", "LUPIN", "OBEROIRLTY", "PRESTIGE", "GODREJPROP", "LODHA", "DLF", "TITAN", "AMBER", "HAVELLS",
        "DIXON", "VOLTAS", "BLUESTARCO", "RELIANCE", "OIL", "ATGL", "ONGC", "BPCL", "PIDILITIND", "SRF", "PIIND", "CGPOWER",
        "NTPC", "TORNTPOWER", "JSWENERGY", "TATAPOWER", "SIEMENS", "ADANIGREEN", "ADANIENSOL", "ABB", "ADANIPOWER",
        "COALINDIA", "POWERINDIA", "HAL", "BEL", "BDL", "BHEL", "MAZDOCK", "COCHINSHIP", "DALBHARAT", "POLYCAB",
        "PNBHOUSING", "DMART", "360ONE", "ETERNAL", "ASIANPAINT", "LT", "HDFCBANK", "BANDHANBNK", "INDUSINDBK", "TECHM",
        "KOTAKBANK", "ICICIBANK", "AXISBANK"
    ]);

export default function StockScanner() {
    const stockScannerRef = useRef(null);
    const stickyTopShellRef = useRef(null);
    const { options, sectorialIndicesData, foTopGainerLooserData,
        atlasScannerData
     } = useStockApiWebsocket(
        environment.stockApiDayRangeDataWSUrl
    );

    const [sectorData, setSectorData] = useState([]);
    const [midCrossScannerData, setMidCrossScannerData] = useState({});
    const [activeTab, setActiveTab] = useState(MID_CROSS_BUY_TAB);

    useEffect(() => {
        setSectorData(Array.isArray(sectorialIndicesData) ? sectorialIndicesData : []);

        if (Array.isArray(sectorialIndicesData) && sectorialIndicesData.length > 0) {
            console.log("Sector Data:", sectorialIndicesData);
        }
    }, [sectorialIndicesData]);

    useEffect(() => {
        const stockScannerElement = stockScannerRef.current;
        const stickyTopShellElement = stickyTopShellRef.current;

        if (!stockScannerElement || !stickyTopShellElement) {
            return undefined;
        }

        const updateLayoutMetrics = () => {
            const stickyTopHeight = Math.ceil(stickyTopShellElement.getBoundingClientRect().height);
            const viewportHeight = Math.ceil(window.visualViewport?.height ?? window.innerHeight);

            stockScannerElement.style.setProperty("--stock-scanner-fixed-top-height", `${stickyTopHeight}px`);
            stockScannerElement.style.setProperty("--stock-scanner-viewport-height", `${viewportHeight}px`);
        };

        updateLayoutMetrics();

        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(updateLayoutMetrics)
            : null;

        resizeObserver?.observe(stickyTopShellElement);
        window.addEventListener("resize", updateLayoutMetrics);
        window.addEventListener("orientationchange", updateLayoutMetrics);
        window.visualViewport?.addEventListener("resize", updateLayoutMetrics);
        window.visualViewport?.addEventListener("scroll", updateLayoutMetrics);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", updateLayoutMetrics);
            window.removeEventListener("orientationchange", updateLayoutMetrics);
            window.visualViewport?.removeEventListener("resize", updateLayoutMetrics);
            window.visualViewport?.removeEventListener("scroll", updateLayoutMetrics);
        };
    }, []);

    useEffect(() => {
        setMidCrossScannerData(
            atlasScannerData && typeof atlasScannerData === "object" && !Array.isArray(atlasScannerData)
                ? atlasScannerData
                : {}
        );

        if (
            atlasScannerData &&
            typeof atlasScannerData === "object" &&
            !Array.isArray(atlasScannerData) &&
            Object.keys(atlasScannerData).length > 0
        ) {
            console.log("Mid Cross Scanner Data:", atlasScannerData);
        }
    }, [atlasScannerData]);

    const getChangeCellClassName = (changePercent) => {
        if (changePercent > 0) return "positive";
        if (changePercent < 0) return "negative";
        return "neutral";
    };

    const getTopAndBottomCHP = (sectors) => {
        if (!Array.isArray(sectors) || sectors.length === 0) return {};

        const sorted = sectors
            .map((sector) => ({
                symbol: sector?.index,
                chp: toNumber(sector?.pChange),
            }))
            .filter((sector) => sector.chp != null)
            .sort((a, b) => a.chp - b.chp);

        if (sorted.length === 0) return {};

        return {
            firstLowest: sorted[0],
            secondLowest: sorted[1],
            thirdLowest: sorted[2],
            firstHighest: sorted[sorted.length - 1],
            secondHighest: sorted[sorted.length - 2],
            thirdHighest: sorted[sorted.length - 3],
        };
    };

    const getScannerHeaderSymbol = (value) =>
        String(value ?? "")
            .split(":")
            .pop()
            ?.split("-")[0]
            ?.trim() || "--";

    const getChpColor = (value) => {
        const num = toNumber(value);
        if (num == null) return "white";
        if (num > 0) return "#0dc80d";
        if (num < 0) return "rgb(228, 67, 136)";
        return "white";
    };

    const stockDayRangeList = Array.isArray(options?.stockDayRangeModelList)
        ? options.stockDayRangeModelList
        : [];
    const lotSizeBySymbol = createLotSizeBySymbolMap(stockDayRangeList);
    const hasMidCrossData = hasScannerEntries(midCrossScannerData);
    const allSectorDetailsBySymbol = createSectorDetailsMap(sectorData, () => true, false);
    const midCrossBuyRows = getScannerRowsByKey(midCrossScannerData, MID_CROSS_BUY);
    const midCrossSellRows = getScannerRowsByKey(midCrossScannerData, MID_CROSS_SELL);
    const midCrossBuyStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(midCrossBuyRows, lotSizeBySymbol),
        allSectorDetailsBySymbol,
        false
    );
    const midCrossSellStocks = attachSectorDetailsToStocks(
        normalizeScannerStocks(midCrossSellRows, lotSizeBySymbol),
        allSectorDetailsBySymbol,
        false
    );
    const foTopGainerRows = getFoTopRowsByKey(foTopGainerLooserData, "FO_TOP_GAINERS");
    const foTopLooserRows = getFoTopRowsByKey(foTopGainerLooserData, "FO_TOP_LOOSERS");
    const foTopGainerSymbolSet = createSymbolSet(foTopGainerRows);
    const foTopLooserSymbolSet = createSymbolSet(foTopLooserRows);
    const rankedSectors = getRankedSectors(sectorData);
    const highestThreeSectors = rankedSectors.slice(-3).map(({ sector }) => sector);
    const lowestThreeSectors = rankedSectors.slice(0, 3).map(({ sector }) => sector);
    const highestThreeSectorSymbolSet = createSectorSymbolSet(highestThreeSectors);
    const lowestThreeSectorSymbolSet = createSectorSymbolSet(lowestThreeSectors);
    const highestThreeSectorNameSet = createSectorNameSet(highestThreeSectors);
    const lowestThreeSectorNameSet = createSectorNameSet(lowestThreeSectors);
    const mcBuyFilterStocks = filterStocksByFoTopAndSectors(
        midCrossBuyStocks,
        foTopGainerSymbolSet,
        highestThreeSectorSymbolSet,
        highestThreeSectorNameSet
    );
    const mcSellFilterStocks = filterStocksByFoTopAndSectors(
        midCrossSellStocks,
        foTopLooserSymbolSet,
        lowestThreeSectorSymbolSet,
        lowestThreeSectorNameSet
    );

    const midCrossBuyCount = midCrossBuyStocks.length;
    const midCrossSellCount = midCrossSellStocks.length;
    const mcFilterCount = mcBuyFilterStocks.length + mcSellFilterStocks.length;
    const activeFixedTitle = activeTab === MID_CROSS_BUY_TAB
        ? {
            label: SCANNER_SECTION_TITLE_MAP[MID_CROSS_BUY],
            count: midCrossBuyCount,
        }
        : activeTab === MID_CROSS_SELL_TAB
            ? {
                label: SCANNER_SECTION_TITLE_MAP[MID_CROSS_SELL],
                count: midCrossSellCount,
            }
            : null;

    useEffect(() => {
        const stockScannerElement = stockScannerRef.current;
        const stickyTopShellElement = stickyTopShellRef.current;

        if (!stockScannerElement || !stickyTopShellElement) {
            return undefined;
        }

        const animationFrameId = window.requestAnimationFrame(() => {
            const stickyTopHeight = Math.ceil(stickyTopShellElement.getBoundingClientRect().height);
            const viewportHeight = Math.ceil(window.visualViewport?.height ?? window.innerHeight);

            stockScannerElement.style.setProperty("--stock-scanner-fixed-top-height", `${stickyTopHeight}px`);
            stockScannerElement.style.setProperty("--stock-scanner-viewport-height", `${viewportHeight}px`);
        });

        return () => window.cancelAnimationFrame(animationFrameId);
    }, [activeTab, midCrossBuyCount, midCrossSellCount, mcFilterCount]);
    const sectorHeaderData = sectorData[0] ?? {};
    const headerDate = sectorHeaderData?.localDate ?? sectorHeaderData?.createdDate ?? sectorHeaderData?.date ?? "--";
    const headerTime = sectorHeaderData?.localTime ?? sectorHeaderData?.createdTime ?? sectorHeaderData?.time ?? "--";
    const scannerHeaderStats = getTopAndBottomCHP(sectorData);
    const topBottomHeaderData = [
        {
            label1: "1st HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.firstHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.firstHighest?.chp ?? "--",
        },
        {
            label1: "2nd HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.secondHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.secondHighest?.chp ?? "--",
        },
        {
            label1: "3rd HS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.thirdHighest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.thirdHighest?.chp ?? "--",
        },
        {
            label1: "1st LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.firstLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.firstLowest?.chp ?? "--",
        },
        {
            label1: "2nd LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.secondLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.secondLowest?.chp ?? "--",
        },
        {
            label1: "3rd LS",
            value1: getScannerHeaderSymbol(scannerHeaderStats?.thirdLowest?.symbol),
            label2: "CHP",
            value2: scannerHeaderStats?.thirdLowest?.chp ?? "--",
        },
    ];
    const renderTableRows = (stocks, emptyMessage, showCount = true, highlightedSymbolSet = new Set()) => {
        if (stocks.length === 0) {
            return (
                <tr>
                    <td colSpan={showCount ? "10" : "9"} className="stock-scanner-empty-cell">
                        {emptyMessage}
                    </td>
                </tr>
            );
        }

        return stocks.map((stock) => {
            const changePercentValue = stock.changePercent ?? 0;
            const isPositive = changePercentValue >= 0;
            const normalizedSymbol = normalizeSymbol(stock.symbol);
            const isHighlightedSymbol = highlightedSymbolSet.has(normalizedSymbol);
            const sectorPercentValue = toNumber(stock.sectorPercent);

            return (
                <tr key={stock.__scannerKey}>
                    <td>{stock.triggerDate}</td>
                    <td>{stock.triggerTime}</td>
                    <td className={isHighlightedSymbol ? "symbol-highlight" : ""}>{stock.symbol}</td>
                    <td>{formatNumber(stock.ltp)}</td>
                    <td className={getChangeCellClassName(changePercentValue)}>
                        {isPositive ? "+" : ""}
                        {formatNumber(changePercentValue)}%
                    </td>
                    {showCount && <td>{stock.symbolCount}</td>}
                    <td>{stock.sectorName ?? "--"}</td>
                    <td className={sectorPercentValue == null ? "" : getChangeCellClassName(sectorPercentValue)}>
                        {sectorPercentValue == null
                            ? "--"
                            : `${sectorPercentValue >= 0 ? "+" : ""}${formatNumber(sectorPercentValue)}%`}
                    </td>
                    <td>{stock.fnoLotSize ?? "--"}</td>
                    <td>{formatNumber(stock.volume)}</td>
                </tr>
            );
        });
    };

    const scannerWaitingMessage = hasMidCrossData
        ? "No matching stocks found."
        : "Waiting for live scanner data...";
    const hasSectorData = sectorData.length > 0;
    const mcBuyFilterWaitingMessage = !hasMidCrossData
        ? scannerWaitingMessage
        : foTopGainerRows.length === 0
            ? "Waiting for live F&O top gainers data..."
            : !hasSectorData
                ? "Waiting for live sector data..."
                : "No matching stocks found.";
    const mcSellFilterWaitingMessage = !hasMidCrossData
        ? scannerWaitingMessage
        : foTopLooserRows.length === 0
            ? "Waiting for live F&O top loosers data..."
            : !hasSectorData
                ? "Waiting for live sector data..."
                : "No matching stocks found.";

    const renderScannerTable = (
        stocks,
        emptyMessage,
        showCount = true,
        highlightedSymbolSet = new Set(),
        useContentHeight = false
    ) => (
        <div className={`table-scroll-area ${useContentHeight ? "mc-filter-table-scroll-area" : ""}`}>
            <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table scanner-table">
                <thead>
                    <tr>
                        <th>DATE</th>
                        <th>TIME</th>
                        <th>SYMBOL</th>
                        <th>LTP</th>
                        <th>CHANGE (%)</th>
                        {showCount && <th>COUNT</th>}
                        <th>SECTOR</th>
                        <th>SECTOR (%)</th>
                        <th>FNO LOT SIZE</th>
                        <th>VOLUME</th>
                    </tr>
                </thead>
                <tbody>{renderTableRows(stocks, emptyMessage, showCount, highlightedSymbolSet)}</tbody>
            </table>
        </div>
    );

    const renderScannerSection = (
        sectionKey,
        stocks,
        emptyMessage = scannerWaitingMessage,
        showTitle = true,
        useContentHeight = false
    ) => (
        <div className="scanner-section-list">
            <div className="scanner-section-block">
                {showTitle && (
                    <div className="scanner-section-title">
                        {SCANNER_SECTION_TITLE_MAP[sectionKey]} ({stocks.length})
                    </div>
                )}
                {renderScannerTable(stocks, emptyMessage, false, new Set(), useContentHeight)}
            </div>
        </div>
    );

    return (
        <div className="stock-scanner" ref={stockScannerRef}>
            <div className="sticky-top-shell" ref={stickyTopShellRef}>
                <header className="header">
                    <nav className="nav-container">
                        <div className="header-flex">
                            <div className="left-nav">
                                <div className="logo-container">
                                    <img
                                        src="/assets/images/noptionwave-logo-1.png"
                                        className="header-logo"
                                        alt="logo"
                                    />
                                </div>

                                {topBottomHeaderData.map((item, idx) => {
                                    const color = getChpColor(item.value2);

                                    return (
                                        <div className="header-item" key={`${item.label1}-${idx}`}>
                                            <span className="header-line" style={{ color }}>
                                                {item.label1} : {item.value1}
                                            </span>
                                            <span className="header-line" style={{ color }}>
                                                {item.label2} : {item.value2}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="right-nav">
                                <span className="header-line" style={{ color: "white" }}>
                                    Date : {headerDate}
                                </span>
                                <span className="header-line" style={{ color: "white" }}>
                                    Time : {headerTime}
                                </span>
                            </div>
                        </div>
                    </nav>
                </header>

                <div className="tab-container">
                    <div className="tab">
                        <button
                            className={`tablinks sub-header-font ${activeTab === MID_CROSS_BUY_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(MID_CROSS_BUY_TAB)}
                        >
                            MID CROSS BUY ({midCrossBuyCount})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === MC_FILTER_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(MC_FILTER_TAB)}
                        >
                            MC FILTER ({mcFilterCount})
                        </button>
                        <button
                            className={`tablinks sub-header-font ${activeTab === MID_CROSS_SELL_TAB ? "active" : ""}`}
                            onClick={() => setActiveTab(MID_CROSS_SELL_TAB)}
                        >
                            MID CROSS SELL ({midCrossSellCount})
                        </button>
                    </div>
                </div>

                {activeFixedTitle && (
                    <div className="fixed-active-scanner-title">
                        {activeFixedTitle.label} ({activeFixedTitle.count})
                    </div>
                )}
            </div>

            <div className="table-container">
                {activeTab === MID_CROSS_BUY_TAB && renderScannerSection(
                    MID_CROSS_BUY,
                    midCrossBuyStocks,
                    scannerWaitingMessage,
                    false
                )}

                {activeTab === MID_CROSS_SELL_TAB && renderScannerSection(
                    MID_CROSS_SELL,
                    midCrossSellStocks,
                    scannerWaitingMessage,
                    false
                )}

                {activeTab === MC_FILTER_TAB && (
                    <>
                        {renderScannerSection(
                            MC_BUY_FILTER_TAB,
                            mcBuyFilterStocks,
                            mcBuyFilterWaitingMessage,
                            true,
                            true
                        )}

                        {renderScannerSection(
                            MC_SELL_FILTER_TAB,
                            mcSellFilterStocks,
                            mcSellFilterWaitingMessage,
                            true,
                            true
                        )}
                    </>
                )}
            </div>

            <div className="fixed-footer">
                <div className="footer-left">
                    <span>noptionwave © 2026</span>
                </div>
            </div>
        </div>
    );
}
